import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloudIcon from '@mui/icons-material/Cloud';
import ChatIcon from '@mui/icons-material/Chat';
import BusinessIcon from '@mui/icons-material/Business';
import flashbackLogo from '../assets/logowhite.png';
import {
  initTutorialProgress,
  startTutorial,
  setTutorialStep,
  completeTutorial,
  TUTORIAL_IDS,
} from '../utils/tutorialProgress';

interface WelcomeTutorialProps {
  open: boolean;
  onComplete: () => void;
  userName?: string;
}

const WelcomeTutorial: React.FC<WelcomeTutorialProps> = ({
  open,
  onComplete,
  userName = 'User',
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const firstName = userName?.split(' ')[0] || 'User';

  // Initialize tutorial progress tracking in localStorage
  useEffect(() => {
    const stepIds = ['welcome', 'services'];
    initTutorialProgress(TUTORIAL_IDS.WELCOME, stepIds);
  }, []);

  // Track when tutorial becomes active
  useEffect(() => {
    if (open) {
      startTutorial(TUTORIAL_IDS.WELCOME);
    }
  }, [open]);

  // Track step changes in localStorage
  useEffect(() => {
    if (open) {
      setTutorialStep(TUTORIAL_IDS.WELCOME, currentStep);
    }
  }, [open, currentStep]);

  // Service cards data - Colors matching Hub doors
  const services = [
    {
      id: 'cloud-gateway',
      title: 'Cloud & AI Gateway',
      description: 'Access your cloud resources, repositories, AI services and infrastructure.',
      icon: <CloudIcon sx={{ fontSize: 28, color: 'white' }} />,
      bgColor: '#F0F9FF',
      borderColor: '#4facfe',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      features: [
        'Multi-cloud storage management',
        'AI model integration (OpenAI, Anthropic, etc.)',
        'Usage analytics & monitoring',
      ],
      requiresOrg: false,
    },
    {
      id: 'private-chat',
      title: 'Private Chat',
      description: 'AI-powered conversational interface for intelligent assistance.',
      icon: <ChatIcon sx={{ fontSize: 28, color: 'white' }} />,
      bgColor: '#F5F3FF',
      borderColor: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      features: [
        'End-to-end encrypted conversations',
        'Organization-scoped AI environment',
        'Multi-model support',
      ],
      requiresOrg: true,
    },
    {
      id: 'organization',
      title: 'Organization',
      description: 'Manage your team, settings, billing and subscription plans.',
      icon: <BusinessIcon sx={{ fontSize: 28, color: 'white' }} />,
      bgColor: '#FFF1F2',
      borderColor: '#f5576c',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      features: [
        'Team & workspace management',
        'Role-based access control',
        'Audit logs & compliance',
      ],
      requiresOrg: true,
    },
  ];

  const handleContinue = () => {
    if (currentStep === 0) {
      setCurrentStep(1);
    } else {
      // Mark tutorial as completed using centralized tracking
      completeTutorial(TUTORIAL_IDS.WELCOME);
      // Keep legacy flag for backward compatibility
      try {
        localStorage.setItem('welcomeTutorialCompleted', 'true');
      } catch { /* ignore */ }
      onComplete();
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Box
        sx={{
          width: '90%',
          maxWidth: currentStep === 0 ? 600 : 900,
          bgcolor: 'white',
          borderRadius: '24px',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          transition: 'max-width 0.3s ease',
        }}
      >
        {currentStep === 0 ? (
          // Step 1: Welcome
          <Box sx={{ p: 5, textAlign: 'center' }}>
            {/* Logo */}
            <Box
              sx={{
                width: 80,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 4,
              }}
            >
              <img src={flashbackLogo} alt="Flashback" style={{ width: 80, height: 80, objectFit: 'contain' }} />
            </Box>

            <Typography
              sx={{
                fontSize: '32px',
                fontWeight: 700,
                color: '#181D27',
                mb: 2,
              }}
            >
              Welcome to Flashback, {firstName}!
            </Typography>

            <Typography
              sx={{
                fontSize: '16px',
                color: '#535862',
                mb: 4,
                maxWidth: 480,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Your all-in-one platform for secure cloud storage, AI integration, and enterprise collaboration. 
              Let's take a quick look at what you can do.
            </Typography>

            <Button
              variant="contained"
              onClick={handleContinue}
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '15px',
                fontWeight: 600,
                borderRadius: '12px',
                bgcolor: '#617BFF',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: '#5068e0',
                },
              }}
            >
              Discover the Platform
            </Button>
          </Box>
        ) : (
          // Step 2: Services presentation
          <Box sx={{ p: 5 }}>
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#181D27',
                mb: 1,
                textAlign: 'center',
              }}
            >
              Three Powerful Services
            </Typography>

            <Typography
              sx={{
                fontSize: '14px',
                color: '#535862',
                mb: 4,
                textAlign: 'center',
              }}
            >
              Everything you need to manage cloud resources, AI, and team collaboration
            </Typography>

            {/* Services Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 3,
                mb: 4,
              }}
            >
              {services.map((service) => (
                <Box
                  key={service.id}
                  sx={{
                    p: 3,
                    borderRadius: '16px',
                    border: `1px solid ${service.borderColor}20`,
                    bgcolor: service.bgColor,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${service.borderColor}20`,
                    },
                  }}
                >
                  {/* Icon with gradient background like Hub */}
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '12px',
                      background: service.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  >
                    {service.icon}
                  </Box>

                  <Typography
                    sx={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#181D27',
                      mb: 1,
                    }}
                  >
                    {service.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: '#535862',
                      mb: 2,
                      lineHeight: 1.5,
                      minHeight: '40px',
                    }}
                  >
                    {service.description}
                  </Typography>

                  {/* Features */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
                    {service.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: service.borderColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                            <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#414651' }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  
                  {/* Organization Account Required note */}
                  {service.requiresOrg && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid rgba(0,0,0,0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2"/>
                        <path d="M7 11V7a5 5 0 0110 0v4"/>
                      </svg>
                      <Typography sx={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 500 }}>
                        Organization Account required
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* Continue Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={handleContinue}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 5,
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 600,
                  borderRadius: '12px',
                  bgcolor: '#617BFF',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#5068e0',
                  },
                }}
              >
                Get Started
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WelcomeTutorial;
